// src/data/changement-adresse.ts
//
// Hub « changement d'adresse à Liège » — LOT 11 du plan SEO.
//
// Une page pilier (la checklist, de J-60 à J+30) et une fiche par opérateur.
// Les données sont dans changement-adresse.json ; ce module les type, les
// expose et dit ce qui est publiable.
//
// LE POINT STRUCTURANT : une fiche opérateur est un enregistrement à champs
// typés — quoi faire, où, délai, documents, relevé de compteur, coût, source,
// date de vérification — et non un texte. C'est ce qui rend sa mise à jour
// mesurable : le contrôle 12f du build lit `dateVerification` et avertit
// au-delà de douze mois, exactement comme pour les fiches stationnement des
// communes. Une procédure administrative qui change se corrige dans une
// colonne, pas dans un paragraphe.
//
// Deux verrous, dans cet ordre :
//
//   · `fiche && publiable`  la page est servie (pages-en-preparation.ts) ;
//   · `verifiePar === 'human'`  la mention « information vérifiée en <mois>
//     auprès de <source> » est rendue. Une fiche relevée mais non relue
//     porte un marqueur « À VALIDER » à la place, et le contrôle 12d refuse
//     de la déverrouiller tant qu'il subsiste.
import donnees from './changement-adresse.json';

export const HUB_CHANGEMENT_ADRESSE = '/changement-adresse-liege';

export interface SourceOfficielle {
  id: string;
  autorite: string;
  /** L'autorité telle qu'elle se lit après « auprès » : « du SPF Intérieur », « de la CILE ». */
  aupresDe?: string;
  libelle: string;
  url: string;
  dateVerification: string;
}

export interface ActionEtape {
  texte: string;
  /** Source officielle du fait cité, par id dans `SOURCES`. */
  sourceId?: string;
  /** Lien interne, quand une page du site détaille l'action. */
  lien?: string;
  /** Slug de l'opérateur concerné : la fiche est liée si elle est publiable. */
  operateur?: string;
  /** Ce qu'il faudrait collecter pour donner un délai ou une procédure. */
  aCollecter?: string;
}

export interface EtapeAdresse {
  periode: string;
  titre: string;
  actions: ActionEtape[];
}

export type CategorieOperateur =
  | 'administration' | 'eau' | 'energie-reseau' | 'energie-fournisseur'
  | 'courrier' | 'telecom' | 'sante' | 'banque' | 'assurance';

export interface FicheOperateur {
  slug: string;
  nom: string;
  nomComplet: string;
  /** Le nom tel qu'il se lit après « auprès de » : « la CILE », « RESA », « bpost ». */
  aupresDe: string;
  categorie: CategorieOperateur;
  role: string;
  /** La fiche existe-t-elle comme page ? Faux tant que rien n'est collecté. */
  fiche: boolean;
  publiable: boolean;
  verifiePar: 'human' | null;
  dateVerification: string | null;
  sourceUrl: string | null;
  libelleSource: string | null;
  sourcesComplementaires?: Array<{ libelle: string; url: string }>;
  urlOfficielle: string | null;
  titreH1?: string;
  titreMeta?: string;
  descriptionMeta?: string;
  /** Passage extractible « À retenir », 40 à 60 mots, rendu en encadré. */
  aRetenir: string | null;
  quoiFaire: string | null;
  ou: string | null;
  delai: string | null;
  documents: string[];
  releveCompteur: boolean | null;
  cout: string | null;
  etapes: Array<{ titre: string; texte: string }>;
  faq: Array<{ q: string; a: string }>;
  aValider: string[];
}

export interface HubAdresse {
  publiable: boolean;
  verifiePar: 'human' | null;
  dateVerification: string | null;
  aValider: string[];
}

interface Donnees {
  hub: HubAdresse;
  sources: SourceOfficielle[];
  etapes: EtapeAdresse[];
  operateurs: FicheOperateur[];
}

// Les verrous valent `null` dans le JSON : sans ce cast, TypeScript infère
// `null` et juge la comparaison à "human" impossible — vrai aujourd'hui, faux
// le jour où Gramme relit une fiche.
const D = donnees as unknown as Donnees;

export const HUB: HubAdresse = D.hub;
export const SOURCES: readonly SourceOfficielle[] = D.sources;
export const ETAPES: readonly EtapeAdresse[] = D.etapes;
export const OPERATEURS: readonly FicheOperateur[] = D.operateurs;

export const LIBELLES_CATEGORIES: Record<CategorieOperateur, string> = {
  administration: 'Administration',
  eau: 'Eau',
  'energie-reseau': 'Réseau électricité et gaz',
  'energie-fournisseur': "Fournisseur d'énergie",
  courrier: 'Courrier',
  telecom: 'Internet, télévision, téléphone',
  sante: 'Santé',
  banque: 'Banque',
  assurance: 'Assurances',
};

/** URL d'une fiche opérateur, sous le hub. Le schéma se fixe une fois. */
export function cheminOperateur(slug: string): string {
  return `${HUB_CHANGEMENT_ADRESSE}/${slug}`;
}

/** Les opérateurs qui ont une page, publiable ou non. */
export function operateursAvecFiche(): FicheOperateur[] {
  return OPERATEURS.filter((o) => o.fiche);
}

/** La fiche d'un slug, seulement si elle existe comme page. */
export function operateurParSlug(slug: string | undefined): FicheOperateur | undefined {
  if (!slug) return undefined;
  return OPERATEURS.find((o) => o.slug === slug && o.fiche);
}

export function sourceParId(id: string | undefined): SourceOfficielle | undefined {
  return id ? SOURCES.find((s) => s.id === id) : undefined;
}

/**
 * La mention de fraîcheur est-elle rendue ?
 *
 * Trois conditions cumulatives, comme pour les fiches stationnement : une
 * source officielle nommée, une date, une relecture humaine. Une fiche qui
 * n'y satisfait pas n'affiche pas « information vérifiée » — elle affiche
 * qu'elle attend de l'être.
 */
export function ficheVerifiee(o: FicheOperateur): boolean {
  return o.verifiePar === 'human' && Boolean(o.sourceUrl) && Boolean(o.libelleSource) && Boolean(o.dateVerification);
}

/** « 2026-09-06 » → « septembre 2026 ». */
export function moisAnnee(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-BE', { month: 'long', year: 'numeric' });
}

export interface ProblemeAdresse {
  ou: string;
  gravite: 'erreur' | 'avertissement';
  message: string;
}

const URL_OFFICIELLE = /^https:\/\/[^/]+\.be(\/|$)/;

/**
 * Les invariants des données, vérifiés par le test et par le contrôle 12f.
 *
 * Ils portent sur la FORME : un fait publié a une source et une date, un
 * délai n'est pas écrit sans source, une étape du pilier qui cite une source
 * cite une source qui existe. Ils ne disent pas si le fait est vrai — c'est
 * la relecture de Gramme qui le dit, et `verifiePar` qui l'enregistre.
 *
 * @param aujourdhui  date du jour, pour la fraîcheur ; injectable par le test.
 */
export function validerChangementAdresse(aujourdhui: Date = new Date()): ProblemeAdresse[] {
  const p: ProblemeAdresse[] = [];
  const limite = new Date(aujourdhui);
  limite.setUTCFullYear(limite.getUTCFullYear() - 1);
  const perimee = (iso: string | null) => Boolean(iso) && new Date(iso as string) < limite;

  const slugs = OPERATEURS.map((o) => o.slug);
  for (const s of new Set(slugs.filter((x, i) => slugs.indexOf(x) !== i))) {
    p.push({ ou: s, gravite: 'erreur', message: `slug présent plusieurs fois` });
  }

  const ids = SOURCES.map((s) => s.id);
  for (const s of SOURCES) {
    if (!URL_OFFICIELLE.test(s.url)) p.push({ ou: `sources.${s.id}`, gravite: 'erreur', message: `url « ${s.url} » n'est pas une URL https d'un domaine .be` });
    if (perimee(s.dateVerification)) p.push({ ou: `sources.${s.id}`, gravite: 'avertissement', message: `source vérifiée il y a plus de 12 mois (${s.dateVerification})` });
  }

  for (const e of ETAPES) {
    for (const a of e.actions) {
      if (a.sourceId && !ids.includes(a.sourceId)) {
        p.push({ ou: `etapes.${e.periode}`, gravite: 'erreur', message: `action « ${a.texte.slice(0, 40)}… » cite une source inconnue : ${a.sourceId}` });
      }
      if (a.operateur && !slugs.includes(a.operateur)) {
        p.push({ ou: `etapes.${e.periode}`, gravite: 'erreur', message: `action « ${a.texte.slice(0, 40)}… » cite un opérateur inconnu : ${a.operateur}` });
      }
      // Un chiffre ou un délai sans source est une affirmation de mémoire :
      // exactement ce que le plan interdit.
      if (!a.sourceId && /\d+ (jours?|semaines?|mois)|\d+,\d\d ?€/.test(a.texte) && !a.lien) {
        p.push({ ou: `etapes.${e.periode}`, gravite: 'erreur', message: `action « ${a.texte.slice(0, 40)}… » cite un délai ou un montant sans source` });
      }
    }
  }

  if (HUB.publiable && (HUB.verifiePar !== 'human' || !HUB.dateVerification)) {
    p.push({ ou: 'hub', gravite: 'erreur', message: 'publiable=true mais verifiePar/dateVerification non renseignés' });
  }

  for (const o of OPERATEURS) {
    const ou = `operateurs.${o.slug}`;
    if (o.verifiePar !== 'human' && o.verifiePar !== null) {
      p.push({ ou, gravite: 'erreur', message: `verifiePar vaut « ${o.verifiePar} » — attendu "human" ou null` });
    }
    if (o.publiable && !o.fiche) {
      p.push({ ou, gravite: 'erreur', message: 'publiable=true sans page (fiche=false)' });
    }
    if (o.fiche) {
      if (!o.sourceUrl || !URL_OFFICIELLE.test(o.sourceUrl)) p.push({ ou, gravite: 'erreur', message: 'fiche sans sourceUrl officielle (https, domaine .be)' });
      if (!o.libelleSource) p.push({ ou, gravite: 'erreur', message: "fiche sans libelleSource — l'ancre du lien doit être rédigée, pas composée" });
      if (!o.dateVerification) p.push({ ou, gravite: 'erreur', message: 'fiche sans dateVerification' });
      if (!o.quoiFaire || !o.ou || !o.delai) p.push({ ou, gravite: 'erreur', message: 'fiche sans quoiFaire, ou ou delai — les champs typés sont obligatoires' });
      if (o.releveCompteur === null) p.push({ ou, gravite: 'erreur', message: 'fiche sans releveCompteur (oui/non)' });
      if (!o.titreH1 || !o.titreMeta || !o.descriptionMeta) p.push({ ou, gravite: 'erreur', message: 'fiche sans titreH1/titreMeta/descriptionMeta' });
      if (o.faq.length < 3 || o.faq.length > 5) p.push({ ou, gravite: 'erreur', message: `faq : ${o.faq.length} question(s) — il en faut entre 3 et 5` });
      if (o.etapes.length < 2) p.push({ ou, gravite: 'erreur', message: 'fiche avec moins de deux étapes' });
      for (const c of o.sourcesComplementaires ?? []) {
        if (!URL_OFFICIELLE.test(c.url)) p.push({ ou, gravite: 'erreur', message: `source complémentaire « ${c.url} » n'est pas une URL https d'un domaine .be` });
      }
      if (o.publiable && !ficheVerifiee(o)) {
        p.push({ ou, gravite: 'erreur', message: 'publiable=true mais fiche non relue (verifiePar, sourceUrl, libelleSource, dateVerification)' });
      }
      if (o.verifiePar === null) {
        p.push({ ou, gravite: 'avertissement', message: 'fiche collectée mais non relue (verifiePar: null) — marqueur « À VALIDER » rendu' });
      }
      if (perimee(o.dateVerification)) {
        p.push({ ou, gravite: 'avertissement', message: `fiche vérifiée il y a plus de 12 mois (${o.dateVerification}) — revérifier sur ${o.sourceUrl}` });
      }
    }
  }
  return p;
}
